import React, { useEffect, useState } from 'react';
import { omapApi } from '../../../services/omapApi';
import './ModelSection.css';

type Provider = { slug: string; label: string; base_url: string; supports_json_mode: boolean };
type Credential = {
  provider: string; base_url: string; model: string; key_last4: string;
  supports_json_mode: boolean; status: string;
  last_tested_at: string | null; last_error: string | null;
};

const ModelSection: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [cred, setCred] = useState<Credential | null>(null);
  const [provider, setProvider] = useState('xai');
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [editingKey, setEditingKey] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    Promise.all([omapApi.getLlmProviders(), omapApi.getLlmCredential()])
      .then(([provs, existing]) => {
        setProviders(provs);
        if (existing) {
          setCred(existing);
          setProvider(existing.provider);
          setBaseUrl(existing.base_url);
          setModel(existing.model);
          setEditingKey(false); // configured: don't force re-entry
        } else if (provs.length > 0) {
          const xai = provs.find((p) => p.slug === 'xai') || provs[0];
          setProvider(xai.slug);
          setBaseUrl(xai.base_url);
        }
      })
      .catch(() => setMsg({ kind: 'err', text: 'Failed to load AI model settings.' }));
  }, []);

  function onProviderChange(slug: string) {
    setProvider(slug);
    const p = providers.find((x) => x.slug === slug);
    if (p && p.base_url) setBaseUrl(p.base_url);
  }

  async function onSave() {
    setBusy(true); setMsg(null);
    try {
      const payload: any = { provider, base_url: baseUrl, model };
      if (editingKey && apiKey) payload.api_key = apiKey;
      const saved = await omapApi.saveLlmCredential(payload);
      setCred(saved);
      setApiKey(''); setEditingKey(false);
      setMsg({ kind: 'ok', text: 'Saved. Use Test Connection to verify it works.' });
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Save failed.' });
    } finally { setBusy(false); }
  }

  async function onTest() {
    setBusy(true); setMsg(null);
    try {
      const r = await omapApi.testLlmCredential();
      if (r.status === 'ok') {
        setMsg({ kind: 'ok', text: 'Connection works.' });
        setCred((c) => (c ? { ...c, status: 'ok', last_error: null } : c));
      } else {
        setMsg({ kind: 'err', text: `Provider rejected the request: ${r.error}` });
        setCred((c) => (c ? { ...c, status: 'failed', last_error: r.error || '' } : c));
      }
    } catch (e: any) {
      setMsg({ kind: 'err', text: 'Test failed. Save a key first.' });
    } finally { setBusy(false); }
  }

  async function onRemove() {
    setBusy(true); setMsg(null);
    try {
      await omapApi.deleteLlmCredential();
      setCred(null); setApiKey(''); setEditingKey(true); setModel('');
      setMsg({ kind: 'ok', text: 'Removed. Your real companies now need a key again.' });
    } finally { setBusy(false); }
  }

  return (
    <div className="model-section">
      <p className="model-intro">
        Connect your own AI provider to analyze your own companies. The sample
        company always works without a key.
      </p>

      <label className="settings-label">Provider</label>
      <select className="settings-select" value={provider}
              onChange={(e) => onProviderChange(e.target.value)}>
        {providers.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
      </select>

      <label className="settings-label">Base URL</label>
      <input className="settings-input" value={baseUrl}
             onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.x.ai/v1" />

      <label className="settings-label">Model</label>
      <input className="settings-input" value={model}
             onChange={(e) => setModel(e.target.value)} placeholder="grok-4-1-fast-reasoning" />

      <label className="settings-label">API Key</label>
      {editingKey ? (
        <input className="settings-input" type="password" value={apiKey}
               onChange={(e) => setApiKey(e.target.value)} placeholder="Paste your key" />
      ) : (
        <div className="model-key-display">
          <span>····{cred?.key_last4}</span>
          {cred?.status === 'ok' && <span className="model-badge ok">Working</span>}
          {cred?.status === 'failed' && <span className="model-badge err">Rejected</span>}
          <button className="settings-btn settings-btn-secondary"
                  onClick={() => setEditingKey(true)}>Replace Key</button>
        </div>
      )}

      {msg && <div className={msg.kind === 'ok' ? 'settings-success' : 'settings-error'}>{msg.text}</div>}

      <div className="model-actions">
        <button className="settings-btn settings-btn-secondary" disabled={busy} onClick={onTest}>
          Test Connection
        </button>
        <button className="settings-btn settings-btn-primary" disabled={busy} onClick={onSave}>
          Save
        </button>
        {cred && (
          <button className="settings-btn settings-btn-secondary" disabled={busy} onClick={onRemove}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default ModelSection;
